import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import Swal from 'sweetalert2';
import Button from 'react-bootstrap/Button';

import { selectStatusByName, selectSectionAddClicksByName } from '@redux/selectors/FormSelector';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';
import HistoryCard from './HistoryCard';

const MAX = 2;
const whiteList = [
  'OriginatingSystemTimestamp',
  'EffectiveDate',
  'MlsChangeType',
  'PreviousValue',
  'NewValue',
  'DOM',
];
const SortableCard = SortableElement(props => (
  <HistoryCard {...props} />
));

const Grid = SortableContainer(({
  fileList,
  fieldsList,
  validateFigField,
  something,
  ...otherProps
}) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          validateFigField={validateFigField}
          fieldsList={fieldsList}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const RepeatableHistory = ({ fields, validateFigField, historyPayload }) => {
  const {
    values, setValues, setFieldValue,
  } = useFormikContext();

  const statusHistory = useSelector(state => selectStatusByName(state, 'History'));
  const addClickCounter = useSelector(state => selectSectionAddClicksByName(state, 'History'));
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const historyCount = sectionsPerRepeatableSection.History.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.History) {
      Object.entries(values.History).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.History]);

  const fieldsList = useMemo(() => {
    const array = [];
    fields.forEach(field => {
      if (whiteList.includes(field.FieldName)) {
        array.push(field);
      }
    });
    return array;
  }, [fields]);

  const buildFormikValues = () => {
    const newValues = {};
    let index = 0;
    historyPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(entry => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: entry.UniqueID,
        OriginatingSystemTimestamp: entry.OriginatingSystemTimestamp,
        EffectiveDate: entry.EffectiveDate,
        MlsChangeType: entry.MlsChangeType,
        PreviousValue: entry.PreviousValue,
        NewValue: entry.NewValue,
        DOM: entry.DOM,
        SortOrder: entry.SortOrder,
        IsDeleted: entry.IsDeleted,
        IsLive: entry.IsLive,
      };
      // ensure correct sort order
      if (!entry.IsDeleted) {
        newValues[uid].SortOrder = index + 1;
        index += 1;
      }
    });
    return newValues;
  };

  useEffect(() => {
    if (historyPayload?.length) {
      const newValues = buildFormikValues();
      setFieldValue('History', {
        ...values.History,
        ...newValues,
      });
    }
  }, [historyPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'History Exceeded',
    html: `The maximum number of history must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addHistory = () => {
    if (historyCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    setValues({
      ...values,
      History: {
        ...values.History,
        [uid]: {
          uid,
          OriginatingSystemTimestamp: '',
          EffectiveDate: '',
          MlsChangeType: '',
          PreviousValue: '',
          NewValue: '',
          DOM: '',
          SortOrder: fileList.length + 1, // fileList excludes deleted entries
          IsDeleted: false,
          IsLive: false,
        },
      },
    });
  };

  // after sort ends derive new order, then set formik state
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    // preserve deleted entries - excluded from fileList
    Object.entries(values.History).forEach(file => {
      if (file[1].IsDeleted) {
        newValues = {
          ...newValues,
          [file[0]]: {
            ...file[1],
          },
        };
      }
    });
    setValues({
      ...values,
      History: { ...newValues },
    });
  };

  useEffect(() => {
    if (addClickCounter) {
      addHistory();
    }
  }, [addClickCounter]);

  return (
    <>
      { statusHistory?.errorMessage && <div className="invalid-feedback">{statusHistory.errorMessage}</div> }
      <Button
        name="btnAddHistory"
        className="float-right"
        onClick={addHistory}
        size="sm"
      >
        <i className="fas fa-plus mr-2" />
        Add History
      </Button>
      <br className="clearfix" />
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        fieldsList={fieldsList}
        validateFigField={validateFigField}
        onSortEnd={onSortEnd}
        useDragHandle
      />
      <br className="clearfix" />
      {(historyCount > MAX) && (
        <>
          <Button
            name="btnAddHistory"
            className="float-right"
            onClick={addHistory}
            size="sm"
          >
            <i className="fas fa-plus mr-2" />
            Add History
          </Button>
          <br className="clearfix" />
        </>
      )}
    </>
  );
};

RepeatableHistory.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  validateFigField: PropTypes.func,
  historyPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableHistory;
